import PropTypes from "prop-types"
import React, { useState } from "react"
import { connect } from "react-redux"
import styled from "styled-components"
import { getFontColorForBackgroundContrast } from "QuorumGrassroots/styled-components/helperFunctions"
import styleWithOrgDesign from "QuorumGrassroots/styled-components/connect"
import { Icon } from "quorumdesign"
import { constants } from "QuorumGrassroots/constants"
import i18n from "i18n"
import SupporterUpdateComponent from "QuorumGrassroots/widgets/Campaign/components/SupporterUpdateComponent"
import Tooltip from "app/static/frontend/components/Tooltip"
import { reset } from "redux-form/immutable"
import { selectUserdata } from "QuorumGrassroots/framework/selectors"
import { clearUserdata } from "QuorumGrassroots/framework/action-creators"

const StyledUserInfoBlock = styleWithOrgDesign(styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: Roboto;
    border-color: ${(props) => getFontColorForBackgroundContrast(props.organizationDesign.primary_color)};
    background-color: transparent;
    color: ${(props) => getFontColorForBackgroundContrast(props.organizationDesign.primary_color)};
    padding-bottom: ${(props) => props.paddingBottom || "0"};

    // Conditional color logic based on campaign type
    ${(props) => {
        const { CampaignType } = DjangIO.app.grassroots.campaign.types
        const isInteractiveCampaign = props.campaignType === CampaignType.personalized_messages.value

        // For interactive campaigns, always use gray color
        if (isInteractiveCampaign) {
            return `
                border-color: ${constants.colors.gray[8]};
                color: ${constants.colors.gray[8]};
                border-bottom: 1px solid #E9ECEF;
                padding-bottom: 1rem;
            `
        } else {
            return `
                color: ${getFontColorForBackgroundContrast(props.organizationDesign.primary_color)};
            `
        }
    }}

    .user-info-left {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding-right: 1rem;
        @media (max-width: 768px) {
            padding-right: 0;
        }
    }

    .profile-icon {
        width: 1.25rem;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        font-size: 1.125rem;
        color: currentColor;
    }

    .user-details {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
    }

    .user-name {
        font-weight: 500;
        font-size: 1rem;
        line-height: 1rem;
        color: currentColor;
    }

    .user-address {
        font-size: 0.85rem;
        line-height: 0.85rem;
        color: currentColor;
    }

    .user-info-right {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .update-link {
        font-size: 0.75rem;
        background: none;
        border: 1px solid currentColor;
        border-radius: 20px;
        cursor: pointer;
        color: currentColor;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.25rem 0.75rem;
        font-family: inherit;
        height: 2rem;

        .button-label {
            white-space: nowrap;
            line-height: normal;
        }

        &:hover {
            opacity: 0.8;
        }
    }

    .clear-data-button {
        background: none;
        border: none;
        cursor: pointer;
        color: currentColor;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        padding: 0;
        font-size: 0.875rem;

        &:hover {
            opacity: 0.8;
        }
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;

        .user-info-left {
            align-items: center;
        }

        .user-info-right {
            align-self: center;
            justify-content: center;
        }
    }
`)

const UserInfoUpdateBlock = ({ campaign, uniqueWidgetId, onSupporterFormToggle, userdata, clearUserdata }) => {
    const t = (key) => i18n.t(key)
    const [showSupporterUpdate, setShowSupporterUpdate] = useState(false)

    const campaignType = campaign?.campaign_type

    const registrationPageIds = (() => {
        if (!campaign?.registration_page) return []
        const regPageUri = campaign.registration_page
        const idFromUri = DjangIO.app.grassroots.models.GrassrootsRegistrationPage.idFromResourceUri(regPageUri)
        return [idFromUri]
    })()

    const additionalSimpleFields = (() => {
        const campaignTypeEnum = campaign?.campaign_type_enum
        if (campaignTypeEnum?.required_registration_fields) {
            return campaignTypeEnum.required_registration_fields
        }
        return []
    })()

    // Only show for returning users with data
    if (!userdata || Object.keys(userdata).length === 0) {
        return null
    }

    // Extract display information from user data
    const displayName =
        userdata.firstname && userdata.lastname
            ? `${userdata.firstname} ${userdata.lastname}`
            : userdata.firstname || userdata.lastname || ""

    const displayAddress = userdata.input_address || userdata.city || userdata.state || ""

    // Don't show if we don't have meaningful data to display
    if (!displayName && !displayAddress) {
        return null
    }

    const handleUpdateClick = () => {
        setShowSupporterUpdate(true)
        if (onSupporterFormToggle) {
            onSupporterFormToggle(true)
        }
    }

    const handleHideSupporterUpdate = () => {
        setShowSupporterUpdate(false)
        if (onSupporterFormToggle) {
            onSupporterFormToggle(false)
        }
    }

    const handleClearData = () => {
        if (window.store) {
            window.store.dispatch((dispatch) => {
                dispatch(clearUserdata())
                dispatch(reset("registration"))
            })
        } else {
            window.location.reload()
        }
    }

    if (showSupporterUpdate) {
        return (
            <SupporterUpdateComponent
                onBackClick={handleHideSupporterUpdate}
                t={t}
                isCampaignPage={true}
                registrationPageIds={registrationPageIds}
                additionalSimpleFields={additionalSimpleFields}
                campaign={campaign}
                uniqueWidgetId={uniqueWidgetId}
            />
        )
    }

    return (
        <StyledUserInfoBlock className="user-info-update-block" campaignType={campaignType}>
            <div className="user-info-left">
                <div className="profile-icon">
                    <Icon icon="user-circle" iconFamily="far" />
                </div>
                <div className="user-details">
                    {displayName && <div className="user-name">{displayName}</div>}
                    {displayAddress && <div className="user-address">{displayAddress}</div>}
                </div>
            </div>
            <div className="user-info-right">
                <button className="update-link" onClick={handleUpdateClick} type="button">
                    <span className="button-label">Update</span>
                </button>
                <Tooltip label="Clear data" placement="bottom">
                    <button
                        type="button"
                        onClick={handleClearData}
                        className="clear-data-button"
                        aria-label="Clear data"
                    >
                        <Icon icon="user-times" iconFamily="far" />
                    </button>
                </Tooltip>
            </div>
        </StyledUserInfoBlock>
    )
}

UserInfoUpdateBlock.propTypes = {
    campaign: PropTypes.object,
    uniqueWidgetId: PropTypes.string,
    onSupporterFormToggle: PropTypes.func,
    userdata: PropTypes.object,
    clearUserdata: PropTypes.func.isRequired,
}

const mapStateToProps = (state, props) => ({
    userdata: selectUserdata(state, props),
})

const mapDispatchToProps = {
    clearUserdata,
}

export default connect(mapStateToProps, mapDispatchToProps)(UserInfoUpdateBlock)
